import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { AttachmentsAPI } from '@/state/slices/attachments.ts';
import { AssignmentAPI } from '@/state/slices/assignment.ts';
import { FeatureFlagAPI } from '@/state/slices/featureFlags.ts';
import { LogItemAPI } from '@/state/slices/logItem.ts';
import { MediaAPI } from '@/state/slices/media.ts';
import { OptionAPI } from '@/state/slices/options.ts';
import { ReportAPI } from '@/state/slices/reports.ts';
import { SettingsAPI } from '@/state/slices/setting.ts';
import { StudiesAPI } from '@/state/slices/studies.ts';
import { UserAPI } from '@/state/slices/users.ts';
import { SearchAPI } from '@/state/slices/search.ts';
import { ViewsAPI } from '@/state/slices/views.ts';
import { PatientAPI } from '@/state/slices/patients.ts';
import { UploadAPI } from '@/state/slices/uploads.ts';
import measurementSessionReducer from '@/state/slices/measurementSession.ts';
import copilotDraftReducer from '@/state/slices/copilot/copilotDraft.ts';
import copilotScansReducer from '@/state/slices/copilot/copilotScans.ts';
import copilotPlotsReducer from '@/state/slices/copilot/copilotPlots.ts';
import copilotComparisonsReducer from '@/state/slices/copilot/copilotComparisons.ts';
import copilotQualityIssuesReducer from '@/state/slices/copilot/copilotQualityIssues.ts';
import { CopilotChatsAPI } from '@/state/slices/copilot/copilotChats.ts';
import { authAPI } from './slices/auth';
import { rtkQueryErrorLogger } from './slices';
/**
 * Redux Store Configuration
 *
 * This store is pre-configured with RTK Query for API calls.
 * To use it in your app, wrap your App component with Provider:
 *
 * import { Provider } from 'react-state';
 * import { store } from 'state/store';
 *
 * <Provider store={store}>
 *   <App />
 * </Provider>
 */
export const store = configureStore({
  reducer: {
    [authAPI.reducerPath]: authAPI.reducer,
    [AttachmentsAPI.reducerPath]: AttachmentsAPI.reducer,
    [AssignmentAPI.reducerPath]: AssignmentAPI.reducer,
    [FeatureFlagAPI.reducerPath]: FeatureFlagAPI.reducer,
    [MediaAPI.reducerPath]: MediaAPI.reducer,
    [LogItemAPI.reducerPath]: LogItemAPI.reducer,
    [OptionAPI.reducerPath]: OptionAPI.reducer,
    [ReportAPI.reducerPath]: ReportAPI.reducer,
    [SettingsAPI.reducerPath]: SettingsAPI.reducer,
    [StudiesAPI.reducerPath]: StudiesAPI.reducer,
    [UserAPI.reducerPath]: UserAPI.reducer,
    [SearchAPI.reducerPath]: SearchAPI.reducer,
    [ViewsAPI.reducerPath]: ViewsAPI.reducer,
    [PatientAPI.reducerPath]: PatientAPI.reducer,
    [UploadAPI.reducerPath]: UploadAPI.reducer,
    [CopilotChatsAPI.reducerPath]: CopilotChatsAPI.reducer,
    measurementSession: measurementSessionReducer,
    copilotDraft: copilotDraftReducer,
    copilotScans: copilotScansReducer,
    copilotPlots: copilotPlotsReducer,
    copilotComparisons: copilotComparisonsReducer,
    copilotQualityIssues: copilotQualityIssuesReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }).concat(
      authAPI.middleware,
      AttachmentsAPI.middleware,
      AssignmentAPI.middleware,
      FeatureFlagAPI.middleware,
      LogItemAPI.middleware,
      MediaAPI.middleware,
      OptionAPI.middleware,
      ReportAPI.middleware,
      SettingsAPI.middleware,
      StudiesAPI.middleware,
      UserAPI.middleware,
      SearchAPI.middleware,
      ViewsAPI.middleware,
      PatientAPI.middleware,
      UploadAPI.middleware,
      CopilotChatsAPI.middleware,
      rtkQueryErrorLogger
    ),
});

setupListeners(store.dispatch);

// Infer types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
